import React, { useState, useEffect } from 'react';
import { Save, Loader2, Plus, X, Sparkles, Building2, Clock, Phone, MessageSquare, User, Zap, HelpCircle, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessProfileForm = ({ token, onProfileSaved, onPromptGenerated, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [profile, setProfile] = useState({
    business_type: '',
    main_services: [],
    price_range: 'varies',
    service_area: '',
    business_hours: '',
    after_hours_message: '',
    urgent_call_handling: '',
    transfer_rules: '',
    common_questions: [],
    tone: 'friendly',
    ai_name: 'Alex',
    phrases_to_use: [],
    phrases_to_avoid: [],
    unique_selling_points: [],
    competitor_info: '',
    additional_info: ''
  });

  // Temp inputs for list fields
  const [newService, setNewService] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [newPhrase, setNewPhrase] = useState('');
  const [newAvoid, setNewAvoid] = useState('');
  const [newUSP, setNewUSP] = useState('');

  const businessTypes = [
    { value: 'auto_repair', label: 'Auto Repair / Mechanic' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'hvac', label: 'HVAC / Heating & Cooling' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'cleaning', label: 'Cleaning / Pressure Washing' },
    { value: 'landscaping', label: 'Landscaping / Lawn Care' },
    { value: 'medical', label: 'Medical / Healthcare' },
    { value: 'dental', label: 'Dental' },
    { value: 'legal', label: 'Legal / Law Firm' },
    { value: 'salon', label: 'Salon / Spa / Beauty' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'restaurant', label: 'Restaurant / Food Service' },
    { value: 'fitness', label: 'Fitness / Gym' },
    { value: 'pet', label: 'Pet Services / Veterinary' },
    { value: 'photography', label: 'Photography' },
    { value: 'construction', label: 'Construction / Contractor' },
    { value: 'general', label: 'Other / General Business' },
  ];

  const toneOptions = [
    { value: 'professional', label: 'Professional', desc: 'Formal, business-like' },
    { value: 'friendly', label: 'Friendly', desc: 'Warm, approachable' },
    { value: 'casual', label: 'Casual', desc: 'Relaxed, conversational' },
    { value: 'luxury', label: 'Luxury', desc: 'Elegant, high-end' },
    { value: 'energetic', label: 'Energetic', desc: 'Upbeat, enthusiastic' },
  ];

  const priceRanges = [
    { value: 'budget', label: 'Budget-Friendly', desc: 'Competitive pricing' },
    { value: 'mid', label: 'Mid-Range', desc: 'Fair market rates' },
    { value: 'premium', label: 'Premium', desc: 'Higher-end services' },
    { value: 'varies', label: 'Varies', desc: 'Depends on service' },
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/business/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business profile saved!');
      if (onProfileSaved) onProfileSaved(profile);
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePrompt = async () => {
    setGenerating(true);
    try {
      // Save profile first
      await axios.put(`${API}/business/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Generate prompt
      const res = await axios.post(`${API}/business/generate-prompt`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Pro prompt generated!');
      if (onPromptGenerated) {
        onPromptGenerated(res.data);
      }
    } catch (err) {
      toast.error('Failed to generate prompt');
    } finally {
      setGenerating(false);
    }
  };

  const addToList = (field, value, setValue) => {
    if (value.trim()) {
      setProfile(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
      setValue('');
    }
  };

  const removeFromList = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${compact ? 'max-w-2xl' : ''}`}>
      {/* Business Type & Basics */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-foreground">Business Basics</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Business Type</label>
            <Select value={profile.business_type} onValueChange={(v) => setProfile({...profile, business_type: v})}>
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="Select your business type" />
              </SelectTrigger>
              <SelectContent>
                {businessTypes.map(bt => (
                  <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Service Area</label>
            <Input
              value={profile.service_area}
              onChange={(e) => setProfile({...profile, service_area: e.target.value})}
              placeholder="e.g., Greater Austin area, 50-mile radius"
              className="bg-background border-border"
            />
          </div>
        </div>

        {/* Main Services */}
        <div className="mt-4">
          <label className="text-sm text-muted-foreground mb-1 block">Main Services (top 5)</label>
          <div className="flex gap-2 mb-2">
            <Input
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              placeholder="e.g., Drain Cleaning"
              className="bg-background border-border"
              onKeyPress={(e) => e.key === 'Enter' && addToList('main_services', newService, setNewService)}
            />
            <Button size="sm" onClick={() => addToList('main_services', newService, setNewService)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.main_services?.map((s, i) => (
              <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm flex items-center gap-1">
                {s}
                <button onClick={() => removeFromList('main_services', i)} className="hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div className="mt-4">
          <label className="text-sm text-muted-foreground mb-2 block">Price Range</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {priceRanges.map(pr => (
              <button
                key={pr.value}
                onClick={() => setProfile({...profile, price_range: pr.value})}
                className={`p-2 rounded-lg text-left border transition-all ${
                  profile.price_range === pr.value
                    ? 'bg-blue-600/20 border-blue-500 text-foreground'
                    : 'bg-muted/30 border-border text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <p className="font-medium text-sm">{pr.label}</p>
                <p className="text-xs opacity-70">{pr.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Business Hours & Availability */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-green-400" />
          <h3 className="font-semibold text-foreground">Hours & Availability</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Business Hours</label>
            <Input
              value={profile.business_hours}
              onChange={(e) => setProfile({...profile, business_hours: e.target.value})}
              placeholder="e.g., Mon-Fri 8am-6pm, Sat 9am-2pm, Closed Sunday"
              className="bg-background border-border"
            />
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">After-Hours Message</label>
            <Textarea
              value={profile.after_hours_message}
              onChange={(e) => setProfile({...profile, after_hours_message: e.target.value})}
              placeholder="What should your Pro say when someone calls after hours?"
              className="bg-background border-border"
              rows={2}
            />
          </div>
        </div>
      </Card>

      {/* Call Handling */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <Phone className="w-5 h-5 text-orange-400" />
          <h3 className="font-semibold text-foreground">Call Handling</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">When should Pro transfer to you?</label>
            <Textarea
              value={profile.transfer_rules}
              onChange={(e) => setProfile({...profile, transfer_rules: e.target.value})}
              placeholder="e.g., Transfer when caller asks for owner, has a complaint, or needs emergency service"
              className="bg-background border-border"
              rows={2}
            />
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">How to handle urgent calls?</label>
            <Textarea
              value={profile.urgent_call_handling}
              onChange={(e) => setProfile({...profile, urgent_call_handling: e.target.value})}
              placeholder="e.g., For emergencies, always try to transfer. If unavailable, get callback number and text me immediately."
              className="bg-background border-border"
              rows={2}
            />
          </div>

          {/* Common Questions */}
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Common Questions Your Customers Ask</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="e.g., Do you offer free estimates?"
                className="bg-background border-border"
                onKeyPress={(e) => e.key === 'Enter' && addToList('common_questions', newQuestion, setNewQuestion)}
              />
              <Button size="sm" onClick={() => addToList('common_questions', newQuestion, setNewQuestion)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1">
              {profile.common_questions?.map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-2 py-1 rounded">
                  <HelpCircle className="w-3 h-3" />
                  <span className="flex-1">{q}</span>
                  <button onClick={() => removeFromList('common_questions', i)} className="hover:text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Pro Personality */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-purple-400" />
          <h3 className="font-semibold text-foreground">Pro Personality</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Pro Name</label>
            <Input
              value={profile.ai_name}
              onChange={(e) => setProfile({...profile, ai_name: e.target.value})}
              placeholder="e.g., Alex, Sarah, Mike"
              className="bg-background border-border"
            />
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Conversation Tone</label>
            <div className="flex flex-wrap gap-2">
              {toneOptions.map(t => (
                <button
                  key={t.value}
                  onClick={() => setProfile({...profile, tone: t.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    profile.tone === t.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Phrases */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Phrases to Use</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newPhrase}
                onChange={(e) => setNewPhrase(e.target.value)}
                placeholder="e.g., We'd be happy to help!"
                className="bg-background border-border text-sm"
                onKeyPress={(e) => e.key === 'Enter' && addToList('phrases_to_use', newPhrase, setNewPhrase)}
              />
              <Button size="sm" onClick={() => addToList('phrases_to_use', newPhrase, setNewPhrase)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {profile.phrases_to_use?.map((p, i) => (
                <span key={i} className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs flex items-center gap-1">
                  "{p}"
                  <button onClick={() => removeFromList('phrases_to_use', i)}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Phrases to Avoid</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newAvoid}
                onChange={(e) => setNewAvoid(e.target.value)}
                placeholder="e.g., I don't know"
                className="bg-background border-border text-sm"
                onKeyPress={(e) => e.key === 'Enter' && addToList('phrases_to_avoid', newAvoid, setNewAvoid)}
              />
              <Button size="sm" onClick={() => addToList('phrases_to_avoid', newAvoid, setNewAvoid)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {profile.phrases_to_avoid?.map((p, i) => (
                <span key={i} className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs flex items-center gap-1">
                  "{p}"
                  <button onClick={() => removeFromList('phrases_to_avoid', i)}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Unique Selling Points */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-yellow-400" />
          <h3 className="font-semibold text-foreground">What Makes You Special?</h3>
        </div>
        
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Unique Selling Points</label>
          <div className="flex gap-2 mb-2">
            <Input
              value={newUSP}
              onChange={(e) => setNewUSP(e.target.value)}
              placeholder="e.g., 24/7 emergency service, Family-owned since 1985"
              className="bg-background border-border"
              onKeyPress={(e) => e.key === 'Enter' && addToList('unique_selling_points', newUSP, setNewUSP)}
            />
            <Button size="sm" onClick={() => addToList('unique_selling_points', newUSP, setNewUSP)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.unique_selling_points?.map((u, i) => (
              <span key={i} className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-sm flex items-center gap-1">
                ⭐ {u}
                <button onClick={() => removeFromList('unique_selling_points', i)}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-sm text-muted-foreground mb-1 block">How to handle competitor mentions?</label>
          <Textarea
            value={profile.competitor_info}
            onChange={(e) => setProfile({...profile, competitor_info: e.target.value})}
            placeholder="e.g., Acknowledge them professionally, focus on our strengths: faster response times and local ownership"
            className="bg-background border-border"
            rows={2}
          />
        </div>
      </Card>

      {/* Additional Info */}
      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <h3 className="font-semibold text-foreground">Additional Information</h3>
        </div>
        
        <Textarea
          value={profile.additional_info}
          onChange={(e) => setProfile({...profile, additional_info: e.target.value})}
          placeholder="Anything else your Pro should know? Special policies, seasonal offers, specific instructions..."
          className="bg-background border-border"
          rows={4}
        />
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Profile
        </Button>
        
        <Button 
          onClick={handleGeneratePrompt} 
          disabled={generating}
          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Generate Prompt
        </Button>
      </div>
    </div>
  );
};

export default BusinessProfileForm;
